import { appLoaderApiClient } from '../app-loader-api-client';
import { appStorage } from '../../storage/app-storage';
import { ApiClientMode, IApiClient } from '../../types/api/api-types';
import { ApiResponse } from '../../../../shared/infrastructure/types/api/api-types';
import { ServiceRestrictionsResponse } from '../../types/common/contracts/bootstrap-contracts';
import {
  CheckPhoneCodeResponse,
  CreateWithAccountHeaders,
  ExecuteAuthCallbackParams,
  ExecuteAuthCallbackRequest,
  ExecuteAuthCallbackResponse,
  LoginRequest,
  LoginResponse,
  ManifestData,
  ProductResponse,
  RecoveryErrorData,
  RecoveryRequestRequest,
  RecoveryResetErrorData,
  RecoveryResetRequest,
  RecoveryResetValidateResponse,
  InvitationValidateResponse,
  InvitationConfirmRequest,
  InvitationErrorData,
  RefreshTokenResponse,
  RegisterErrorData,
  RegisterRequest,
  RegisterAccountResponse,
  RequestPhoneVerificationParams,
  RequestPhoneVerificationWireParams,
  RequestPhoneVerificationResponse,
  PhoneVerificationRetry,
  PhoneVerificationErrorData,
  ShardResponse,
  TemporarySignInRequest,
  UsersMeRequest,
  UsersMeResponse,
  RequestVerificationPrepareResponse,
  CheckCodeByIDResponse,
} from '../../types/features/auth/auth-contracts';

class AppLoaderCommonDatasource {
  private apiClient: IApiClient;

  constructor(apiClient: IApiClient) {
    this.apiClient = apiClient;
  }

  async logout(): Promise<ApiResponse<unknown>> {
    return await this.apiClient.delete(
      `/users/logout` as UrlPath,
      undefined,
      ApiClientMode.SUPREME,
    );
  }

  async getServiceRestrictions(): Promise<ApiResponse<ServiceRestrictionsResponse>> {
    return await this.apiClient.get<ServiceRestrictionsResponse, undefined>(
      '/bootstrap_user' as UrlPath,
    );
  }

  async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return await this.apiClient.post<LoginRequest, LoginResponse>(
      '/users/login' as UrlPath,
      data,
      ApiClientMode.SUPREME,
    );
  }

  async executeAuthCallback(
    data: ExecuteAuthCallbackRequest,
  ): Promise<ApiResponse<ExecuteAuthCallbackResponse>> {
    return await this.apiClient.post<ExecuteAuthCallbackParams, ExecuteAuthCallbackResponse>(
      data.auth_callback as unknown as UrlPath,
      data.params,
      ApiClientMode.ORIGIN,
      undefined,
      { ...data.headers },
    );
  }

  async getUsersMe(params?: UsersMeRequest): Promise<ApiResponse<UsersMeResponse>> {
    const filteredParams = params?.access_token ? { access_token: params.access_token } : undefined;

    return await this.apiClient.get<UsersMeResponse, typeof filteredParams>(
      '/users/me' as UrlPath,
      filteredParams,
      ApiClientMode.SUPREME,
      {},
    );
  }

  async getShard(accountId: AccountID): Promise<ApiResponse<ShardResponse>> {
    return await this.apiClient.get<ShardResponse, undefined>(
      `/accounts/${accountId}/shard` as UrlPath,
      undefined,
      ApiClientMode.ORIGIN,
    );
  }

  async refreshToken(): Promise<ApiResponse<RefreshTokenResponse>> {
    return await this.apiClient.post<undefined, RefreshTokenResponse>(
      '/users/tokens.json' as UrlPath,
      undefined,
      ApiClientMode.SUPREME,
    );
  }

  async getProduct(host: string): Promise<ApiResponse<ProductResponse>> {
    return await this.apiClient.get<ProductResponse, undefined>(
      `/products/${host}.json?ts=${new Date().getTime()}` as UrlPath,
      undefined,
      ApiClientMode.SUPREME,
    );
  }

  async temporarySignIn(data: TemporarySignInRequest): Promise<ApiResponse<LoginResponse>> {
    return await this.apiClient.post<TemporarySignInRequest, LoginResponse>(
      '/api/private/user_temporary_sessions/sign_in' as UrlPath,
      data,
      ApiClientMode.SUPREME,
    );
  }

  async getManifest(url: Url): Promise<ApiResponse<ManifestData>> {
    return await this.apiClient.httpClient.get<ManifestData>(url);
  }

  private serializeVerificationMetadata(
    metadata?: RequestPhoneVerificationParams['metadata'],
  ): string | undefined {
    return metadata && Object.keys(metadata).length > 0 ? JSON.stringify(metadata) : undefined;
  }

  async requestPhoneVerification(
    params: Omit<RequestPhoneVerificationParams, 'product'>,
    retry?: PhoneVerificationRetry,
  ): Promise<ApiResponse<RequestPhoneVerificationResponse, PhoneVerificationErrorData>> {
    const fullParams: RequestPhoneVerificationWireParams = {
      ...params,
      template: retry ? undefined : params.template,
      metadata: retry ? undefined : this.serializeVerificationMetadata(params.metadata),
      product: appStorage.product?.id as ProductId,
    };
    const path = retry ? `/verifications/${retry.registrationId}/request` : '/verifications';
    const customHeaders = retry?.accessToken
      ? { Authorization: `Bearer ${retry.accessToken}` }
      : undefined;

    return await this.apiClient.post<
      undefined,
      RequestPhoneVerificationResponse,
      PhoneVerificationErrorData
    >(
      path as UrlPath,
      undefined,
      ApiClientMode.PRODUCT_ORIGIN,
      undefined,
      customHeaders,
      fullParams,
    );
  }

  async requestVerificationPrepare(
    params: RequestPhoneVerificationParams,
  ): Promise<ApiResponse<RequestVerificationPrepareResponse, PhoneVerificationErrorData>> {
    const path = `/api/verifications/prepare`;
    const wireParams: RequestPhoneVerificationWireParams = {
      ...params,
      metadata: this.serializeVerificationMetadata(params.metadata),
    };

    return await this.apiClient.post<
      RequestPhoneVerificationWireParams,
      RequestVerificationPrepareResponse,
      PhoneVerificationErrorData
    >(path as UrlPath, wireParams, ApiClientMode.PRODUCT_ORIGIN);
  }

  async checkCodeByID(
    id: string,
    token: string,
  ): Promise<ApiResponse<CheckCodeByIDResponse, unknown>> {
    const path = `/verifications/${id}`;
    const customHeaders = { Authorization: `Bearer ${token}` };

    return await this.apiClient.get<CheckCodeByIDResponse, unknown>(
      path as UrlPath,
      undefined,
      ApiClientMode.PRODUCT_ORIGIN,
      customHeaders,
    );
  }

  async checkPhoneCode(
    registrationId: string,
    code: PhoneCode,
    accessToken?: string,
  ): Promise<ApiResponse<CheckPhoneCodeResponse, PhoneVerificationErrorData>> {
    const customHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
    return await this.apiClient.post<undefined, CheckPhoneCodeResponse, PhoneVerificationErrorData>(
      `/verifications/${registrationId}/check` as UrlPath,
      undefined,
      ApiClientMode.PRODUCT_ORIGIN,
      undefined,
      customHeaders,
      { code },
    );
  }

  async register(
    data: RegisterRequest,
  ): Promise<ApiResponse<RegisterAccountResponse, RegisterErrorData>> {
    return await this.apiClient.post<RegisterRequest, RegisterAccountResponse, RegisterErrorData>(
      '/accounts' as UrlPath,
      data,
      ApiClientMode.SUPREME,
    );
  }

  async requestRecoveryEmail(
    data: RecoveryRequestRequest,
  ): Promise<ApiResponse<unknown, RecoveryErrorData>> {
    return await this.apiClient.post<RecoveryRequestRequest, unknown, RecoveryErrorData>(
      '/users/password' as UrlPath,
      data,
      ApiClientMode.SUPREME,
    );
  }

  async resetPassword(
    data: RecoveryResetRequest,
  ): Promise<ApiResponse<unknown, RecoveryResetErrorData>> {
    return await this.apiClient.put<RecoveryResetRequest, unknown, RecoveryResetErrorData>(
      '/users/password' as UrlPath,
      data,
      ApiClientMode.SUPREME,
    );
  }

  async validateResetPasswordToken(
    token: string,
  ): Promise<ApiResponse<RecoveryResetValidateResponse, RecoveryResetErrorData>> {
    return await this.apiClient.get<
      RecoveryResetValidateResponse,
      { reset_password_token: string },
      RecoveryResetErrorData
    >(
      '/users/password/edit.json' as UrlPath,
      { reset_password_token: token },
      ApiClientMode.SUPREME,
    );
  }

  async validateInvitation(
    token: string,
  ): Promise<ApiResponse<InvitationValidateResponse, InvitationErrorData>> {
    return await this.apiClient.get<InvitationValidateResponse, undefined, InvitationErrorData>(
      `/invitations/${token}.json` as UrlPath,
      undefined,
      ApiClientMode.SUPREME,
    );
  }

  async confirmInvitation(
    token: string,
    data: InvitationConfirmRequest,
  ): Promise<ApiResponse<unknown, InvitationErrorData>> {
    return await this.apiClient.post<InvitationConfirmRequest, unknown, InvitationErrorData>(
      `/invitations/${token}/confirm.json` as UrlPath,
      data,
      ApiClientMode.SUPREME,
    );
  }

  async createWithAccount(
    accountsUrl: string,
    data: unknown,
    headers: CreateWithAccountHeaders,
  ): Promise<ApiResponse<unknown, RegisterErrorData>> {
    const normalizedPath = accountsUrl.startsWith('/') ? accountsUrl : `/${accountsUrl}`;
    const path = `${normalizedPath}/create_with_account` as UrlPath;
    return await this.apiClient.post<unknown, unknown, RegisterErrorData>(
      path,
      data,
      ApiClientMode.ORIGIN,
      undefined,
      {
        'Content-Type': headers['content-type'],
        Authorization: headers.authorization,
      },
    );
  }
}

const appLoaderCommonDatasource = new AppLoaderCommonDatasource(appLoaderApiClient);

export { appLoaderCommonDatasource };
